/**
 * HVM Promo Engine — Gamified Gift Progress Bar
 *
 * Lógica:
 * - Cuenta productos elegibles (colección 'vitaminas') excluyendo regalos
 * - Tier 1: 2 productos → 1 regalo gratis
 * - Tier 2: 3+ productos → 2 regalos gratis
 * - Auto-agrega/remueve regalos vía Cart API
 * - Actualiza la UI del progress bar y selector de regalos
 */
(function () {
  const STORAGE_KEY = "hvm_gift_selections";
  const GIFT_PROP = "_is_promo_gift";

  class HVMPromoEngine {
    constructor() {
      this.config = null;
      this.isEvaluating = false; // bloquea evaluaciones concurrentes
      this.isSelecting = false; // bloquea selecciones concurrentes
      this.isRefreshing = false; // bandera para que el observer ignore cambios propios
      this.selections = { 1: null, 2: null }; // slot → variantId
      this.currentTier = 0;
    }

    init() {
      // Siempre registrar listeners (el config puede no existir aún si el carrito está vacío)
      this.bindGiftButtons();
      this.subscribeToCartUpdates();

      // Intentar cargar config (puede fallar si el carrito está vacío, se reintenta en cada acción)
      if (this.tryLoadConfig()) {
        this.restoreSelections();
        setTimeout(() => this.evaluateCart(), 300);
      }
    }

    tryLoadConfig() {
      if (this.config) return true;
      const configEl = document.getElementById("hvm-promo-config");
      if (!configEl) return false;
      try {
        this.config = JSON.parse(configEl.textContent);
        return true;
      } catch (e) {
        console.error("[HVM Promo] Config parse error:", e);
        return false;
      }
    }

    // ── Persistencia ──

    restoreSelections() {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed["1"]) this.selections[1] = parseInt(parsed["1"]);
          if (parsed["2"]) this.selections[2] = parseInt(parsed["2"]);
        }
      } catch (e) {
        /* ignore */
      }
    }

    saveSelections() {
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            1: this.selections[1],
            2: this.selections[2],
          }),
        );
      } catch (e) {
        /* ignore */
      }
    }

    // ── Eventos ──

    bindGiftButtons() {
      document.addEventListener("click", (e) => {
        const btn = e.target.closest(".hvm-promo__gift-btn");
        if (!btn || btn.disabled) return;
        e.preventDefault();

        const slot = parseInt(btn.dataset.slot);
        const variantId = parseInt(btn.dataset.variantId);

        // Lazy config load (para cuando el carrito estaba vacío al cargar la página)
        if (!this.config && !this.tryLoadConfig()) {
          console.log("[HVM Promo] Config not available yet");
          return;
        }

        console.log("[HVM Promo] Gift click:", { slot, variantId, isSelecting: this.isSelecting });

        if (!variantId || isNaN(variantId)) {
          console.error("[HVM Promo] Invalid variantId:", btn.dataset.variantId);
          return;
        }
        if (this.selections[slot] === variantId) return; // ya seleccionado
        this.onGiftSelect(slot, variantId);
      });
    }

    subscribeToCartUpdates() {
      if (
        typeof subscribe === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === "hvm-promo") return;
          // Esperar a que el DOM se actualice
          setTimeout(() => this.evaluateCart(), 400);
        });
      }

      // Fallback: observar cambios solo en cart-drawer-items (no subtree completo)
      const cartDrawerItems = document.querySelector("cart-drawer-items");
      if (cartDrawerItems && cartDrawerItems.parentNode) {
        const observer = new MutationObserver((mutations) => {
          if (this.isRefreshing || this.isEvaluating || this.isSelecting) return;
          for (const m of mutations) {
            if (m.type === "childList" && m.addedNodes.length > 0) {
              setTimeout(() => this.evaluateCart(), 400);
              break;
            }
          }
        });
        // Observar solo el padre directo para detectar cuando cart-drawer-items se reemplaza
        observer.observe(cartDrawerItems.parentNode, {
          childList: true,
          subtree: false,
        });
      }
    }

    // ── Core ──

    async evaluateCart() {
      if (this.isEvaluating || this.isSelecting) return;
      if (!this.config && !this.tryLoadConfig()) return;
      this.isEvaluating = true;

      try {
        const cart = await this.fetchCart();
        const { eligibleCount, giftItems, totalCents, itemsSubtotalCents } =
          this.analyzeCart(cart);
        const cartCents = this.config.useItemsSubtotal
          ? itemsSubtotalCents
          : totalCents;

        // Determinar tier
        let tier = 0;
        let giftsAllowed = 0;
        if (eligibleCount >= 3) {
          tier = 2;
          giftsAllowed = 2;
        } else if (eligibleCount >= 2) {
          tier = 1;
          giftsAllowed = 1;
        }

        this.currentTier = tier;

        // Reconciliar regalos: remover excedentes
        if (giftItems.length > giftsAllowed) {
          const excess = giftItems.length - giftsAllowed;
          for (let i = 0; i < excess; i++) {
            const itemToRemove = giftItems[giftItems.length - 1 - i];
            await this.removeGift(itemToRemove.key);
          }
          // Limpiar selecciones para slots removidos
          for (let s = giftsAllowed + 1; s <= 2; s++) {
            this.selections[s] = null;
          }
          this.saveSelections();
          await this.refreshCartDrawer();
          this.isEvaluating = false;
          return;
        }

        // Si tier subió y hay selecciones guardadas, auto-agregar regalos faltantes
        if (giftItems.length < giftsAllowed) {
          let added = false;
          for (let slot = 1; slot <= giftsAllowed; slot++) {
            const savedVariant = this.selections[slot];
            if (savedVariant && !this.isGiftInCart(giftItems, savedVariant)) {
              await this.addGift(savedVariant);
              added = true;
            }
          }
          if (added) {
            await this.refreshCartDrawer();
            this.isEvaluating = false;
            return;
          }
        }

        // Sincronizar selecciones con lo que hay en el carrito
        this.syncSelectionsFromCart(giftItems);

        // Actualizar UI
        const fsAchieved =
          this.config.fsThresholdCents > 0 &&
          cartCents >= this.config.fsThresholdCents;
        this.updateUI({
          tier,
          eligibleCount,
          fsAchieved,
          cartCents,
          giftItems,
        });
      } catch (e) {
        console.error("[HVM Promo] evaluateCart error:", e);
      } finally {
        this.isEvaluating = false;
      }
    }

    async onGiftSelect(slot, variantId) {
      if (this.isSelecting) return;
      this.isSelecting = true;

      // Feedback visual inmediato
      this.selections[slot] = variantId;
      this.saveSelections();
      this.updateGiftButtonsUI();
      this.setButtonLoading(slot, true);

      try {
        const cart = await this.fetchCart();
        const { giftItems } = this.analyzeCart(cart);

        // Si este slot ya tenía un regalo diferente, removerlo
        const oldGifts = giftItems.filter(
          (g) => g.variant_id !== variantId
        );
        for (const oldItem of oldGifts) {
          // Solo remover si no es el regalo del otro slot
          const otherSlot = slot === 1 ? 2 : 1;
          if (this.selections[otherSlot] !== oldItem.variant_id) {
            await this.removeGift(oldItem.key);
          }
        }

        // Agregar el nuevo regalo si no está en el carrito
        const existingGift = giftItems.find((g) => g.variant_id === variantId);
        if (existingGift) {
          // Verificar si ambos slots tienen el mismo variant
          const otherSlot = slot === 1 ? 2 : 1;
          if (this.selections[otherSlot] === variantId && existingGift.quantity < 2) {
            await this.changeGiftQty(existingGift.key, 2);
          }
        } else {
          await this.addGift(variantId);
        }

        // Refrescar carrito
        await this.refreshCartDrawer();
      } catch (e) {
        console.error("[HVM Promo] onGiftSelect error:", e);
      } finally {
        this.setButtonLoading(slot, false);
        this.isSelecting = false;
      }
    }

    // ── Cart API ──

    async fetchCart() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      return response.json();
    }

    async addGift(variantId) {
      await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: { [GIFT_PROP]: "true" },
            },
          ],
        }),
      });
    }

    async removeGift(lineKey) {
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
        }),
      });
    }

    async changeGiftQty(lineKey, qty) {
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: lineKey,
          quantity: qty,
        }),
      });
    }

    async refreshCartDrawer() {
      // Re-render cart drawer vía section rendering
      this.isRefreshing = true;
      try {
        const response = await fetch(
          `${window.Shopify.routes.root}cart?section_id=cart-drawer`,
        );
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");

        // Reemplazar cart items y footer
        const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
        for (const sel of selectors) {
          const target = document.querySelector(sel);
          const source = doc.querySelector(sel);
          if (target && source) {
            target.replaceWith(source);
          }
        }

        // Reemplazar promo wrapper (para que Liquid recalcule el estado)
        const promoTarget = document.getElementById("hvm-promo-wrapper");
        const promoSource = doc.getElementById("hvm-promo-wrapper");
        if (promoTarget && promoSource) {
          promoTarget.replaceWith(promoSource);
        }

        // Re-aplicar selecciones visuales
        this.updateGiftButtonsUI();

        // Actualizar cart icon
        const bubbleRes = await fetch(
          `${window.Shopify.routes.root}cart?section_id=cart-icon-bubble`,
        );
        const bubbleHtml = await bubbleRes.text();
        const bubbleDoc = new DOMParser().parseFromString(
          bubbleHtml,
          "text/html",
        );
        const bubbleTarget = document.getElementById("cart-icon-bubble");
        const bubbleSource = bubbleDoc.querySelector(".shopify-section");
        if (bubbleTarget && bubbleSource) {
          bubbleTarget.innerHTML = bubbleSource.innerHTML;
        }

        // Actualizar el empty state del cart drawer
        const cartDrawer = document.querySelector("cart-drawer");
        const freshCart = await this.fetchCart();
        if (cartDrawer) {
          cartDrawer.classList.toggle("is-empty", freshCart.item_count === 0);
        }

        // Publicar evento para otros listeners
        if (
          typeof publish === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "hvm-promo",
            cartData: freshCart,
          });
        }
      } catch (e) {
        console.error("[HVM Promo] refreshCartDrawer error:", e);
      } finally {
        // Esperar a que el DOM se estabilice antes de desactivar la bandera
        setTimeout(() => {
          this.isRefreshing = false;
        }, 500);
      }
    }

    // ── Análisis ──

    analyzeCart(cart) {
      let eligibleCount = 0;
      const giftItems = [];
      const eligibleHandles = this.config.eligibleHandles || [];

      for (const item of cart.items) {
        if (item.properties && item.properties[GIFT_PROP] === "true") {
          giftItems.push(item);
        } else if (eligibleHandles.includes(item.handle)) {
          eligibleCount += item.quantity;
        }
      }

      return {
        eligibleCount,
        giftItems,
        totalCents: cart.total_price,
        itemsSubtotalCents: cart.items_subtotal_price,
      };
    }

    isGiftInCart(giftItems, variantId) {
      return giftItems.some((g) => g.variant_id === variantId);
    }

    syncSelectionsFromCart(giftItems) {
      // Si hay regalos en el carrito pero no en las selecciones, sincronizar
      let slotIndex = 1;
      for (const gift of giftItems) {
        for (let q = 0; q < gift.quantity; q++) {
          if (slotIndex <= 2) {
            this.selections[slotIndex] = gift.variant_id;
            slotIndex++;
          }
        }
      }
      // Limpiar slots no usados
      while (slotIndex <= 2) {
        this.selections[slotIndex] = null;
        slotIndex++;
      }
      this.saveSelections();
    }

    // ── UI Updates ──

    updateUI({ tier, eligibleCount, fsAchieved, cartCents, giftItems }) {
      const wrapper = document.getElementById("hvm-promo-wrapper");
      if (!wrapper) return;

      // Fill de la barra
      let fill = 5;
      if (this.config.fsThresholdCents > 0 && !fsAchieved) {
        fill = Math.min(
          Math.max((cartCents / this.config.fsThresholdCents) * 30, 5),
          30,
        );
      } else if (fsAchieved) {
        fill = 33;
      }
      if (eligibleCount === 1 && fsAchieved) fill = 50;
      if (eligibleCount >= 2) fill = 66;
      if (eligibleCount >= 3) fill = 100;

      const fillEl = document.getElementById("hvmPromoFill");
      if (fillEl) fillEl.style.width = `${fill}%`;

      // Nodos
      const nodes = wrapper.querySelectorAll(".hvm-promo__node");
      nodes.forEach((node) => {
        node.classList.remove(
          "hvm-promo__node--unlocked",
          "hvm-promo__node--current",
        );
        const idx = parseInt(node.dataset.node);

        if (idx === 0) {
          if (fsAchieved) node.classList.add("hvm-promo__node--unlocked");
        } else if (idx === 1) {
          if (tier >= 1) node.classList.add("hvm-promo__node--unlocked");
          else if (fsAchieved && eligibleCount < 2)
            node.classList.add("hvm-promo__node--current");
        } else if (idx === 2) {
          if (tier >= 2) node.classList.add("hvm-promo__node--unlocked");
          else if (tier === 1) node.classList.add("hvm-promo__node--current");
        }
      });

      // Headline: último step desbloqueado (oculto si no hay FS)
      const label = document.getElementById("hvmPromoLabel");
      if (label) {
        label.classList.toggle("hvm-promo__label--hidden", !fsAchieved);
        if (tier >= 2) {
          label.innerHTML = "🎉 <strong>2 regalos</strong> desbloqueados";
        } else if (tier === 1) {
          label.innerHTML = "🎉 <strong>1 regalo desbloqueado</strong>";
        } else if (fsAchieved) {
          label.innerHTML = "🎉 <strong>Envío gratis</strong> desbloqueado";
        }
      }

      // Upsell: siguiente paso a desbloquear
      const upsell = document.getElementById("hvmPromoUpsell");
      if (upsell) {
        upsell.classList.toggle("hvm-promo__upsell--hidden", tier >= 2);
        if (!fsAchieved && this.config.fsThresholdCents > 0) {
          const remaining = this.config.fsThresholdCents - cartCents;
          upsell.querySelector(".hvm-promo__upsell-text").innerHTML =
            `Agrega <strong>${this.formatMoney(remaining)}</strong> y desbloquea envío gratis`;
        } else if (tier === 0) {
          const needed = 2 - eligibleCount;
          const plural = needed !== 1 ? "s" : "";
          upsell.querySelector(".hvm-promo__upsell-text").innerHTML =
            `Agrega <strong>${needed} producto${plural} más</strong> y desbloquea tu primer regalo`;
        } else if (tier === 1) {
          upsell.querySelector(".hvm-promo__upsell-text").innerHTML =
            "Agrega <strong>1 producto más</strong> y desbloquea tu segundo regalo";
        }
      }

      // Gift selector visibility
      const giftSel = document.getElementById("hvmGiftSelector");
      if (giftSel) {
        giftSel.classList.toggle("hvm-promo__gifts--hidden", tier < 1);
      }

      const slot1 = document.getElementById("hvmGiftSlot1");
      if (slot1) slot1.classList.remove("hvm-promo__gift-slot--hidden");

      const slot2 = document.getElementById("hvmGiftSlot2");
      if (slot2)
        slot2.classList.toggle("hvm-promo__gift-slot--hidden", tier < 2);

      // Actualizar botones seleccionados
      this.updateGiftButtonsUI();

      // Actualizar data attributes
      wrapper.dataset.tier = tier;
      wrapper.dataset.eligible = eligibleCount;
      wrapper.dataset.fsAchieved = fsAchieved;
      wrapper.dataset.gifts = giftItems.length;
    }

    updateGiftButtonsUI() {
      const wrapper = document.getElementById("hvm-promo-wrapper");
      if (!wrapper) return;

      wrapper.querySelectorAll(".hvm-promo__gift-btn").forEach((btn) => {
        const slot = parseInt(btn.dataset.slot);
        const variantId = parseInt(btn.dataset.variantId);
        btn.classList.toggle(
          "is-selected",
          this.selections[slot] === variantId,
        );
      });
    }

    setButtonLoading(slot, loading) {
      const wrapper = document.getElementById("hvm-promo-wrapper");
      if (!wrapper) return;

      wrapper
        .querySelectorAll(`.hvm-promo__gift-btn[data-slot="${slot}"]`)
        .forEach((btn) => {
          btn.classList.toggle("is-loading", loading);
        });
    }

    formatMoney(cents) {
      if (!this.config.currencyFormat) return `$${(cents / 100).toFixed(0)}`;
      const amount = cents / 100;
      return this.config.currencyFormat
        .replace("{{amount}}", amount.toFixed(2))
        .replace("{{amount_no_decimals}}", amount.toFixed(0))
        .replace(
          "{{amount_with_comma_separator}}",
          amount.toFixed(2).replace(".", ","),
        )
        .replace(
          "{{amount_no_decimals_with_comma_separator}}",
          amount.toFixed(0),
        );
    }
  }

  // Iniciar cuando el DOM esté listo
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () =>
      new HVMPromoEngine().init(),
    );
  } else {
    new HVMPromoEngine().init();
  }
})();
